import { useCallback, useState } from 'react';
import { useTimeoutEffect } from '@react-hookz/web';

// Copy a string to the clipboard with an auto-resetting "copied" flag.
//
// Why this hook exists:
//   1. `navigator.clipboard` is undefined in insecure contexts (non-https, some
//      iframes) and `writeText` can reject on permission denial — callers
//      forget the guard and get uncaught errors. Centralizing the guard here
//      keeps call sites clean.
//   2. The "I copied something" UI state needs to auto-reset after a short
//      window. Inline setTimeouts leak if the component unmounts mid-window;
//      `useTimeoutEffect` cancels automatically on unmount and whenever the
//      delay argument changes (so each new copy restarts the window).
//
// Returns:
//   - `copiedValue`: the last successfully copied string, or null. Falls back
//     to null `resetMs` after the copy so UIs can switch icon/label back.
//   - `copy(value)`: attempts the write; returns true on success, false when
//     the API is unavailable or the write rejected.
export function useCopyToClipboard(resetMs = 2000) {
  const [copiedValue, setCopiedValue] = useState<string | null>(null);

  // Passing `undefined` while no value is copied disables the timer; once a
  // value lands, the hook swaps in a real delay and starts the timeout.
  useTimeoutEffect(
    () => setCopiedValue(null),
    copiedValue === null ? undefined : resetMs,
  );

  const copy = useCallback(async (value: string): Promise<boolean> => {
    const clipboard = globalThis.navigator?.clipboard;
    if (!clipboard?.writeText) {
      return false;
    }
    try {
      await clipboard.writeText(value);
    } catch {
      return false;
    }
    setCopiedValue(value);
    return true;
  }, []);

  return { copiedValue, copy };
}
